import * as _ from 'lodash'
import {IBusinessObject} from '../PA.Core/PA.Core.IF.BO';
import {IMessageProvider, IProgressProvider, MessageType} from '../PA.Core/PA.Core.IF.Basic';
import {PAdbContext} from '../PA.Core/PAdbContext';
import {IdentityProvider} from '../PA.Core/Basic/Identity'
import {MessageProvider} from '../PA.Core/Basic/Messages'
import {ProgressProvider} from '../PA.Core/Basic/Progress'
import {
    WB_BusinessUnitRow,
    WB_ClaimRow, WB_ClaimTypeRow, WB_CustomerRow, WB_PaymentClaimRow, WB_PaymentTypeRow, WB_WorkflowStatusRow
} from "../PA.Core/PAdbSchemaRows";
import {
    esWB_Customer,
    esWB_Claim,
    esWB_Claim_Type,
    esWB_User,
    esWB_Workflow_Status,
    esWB_Payment_Claim,
    esWB_Payment,
    esWB_BusinessUnit, esWB_Payment_Type
} from "../PA.Core/PAdbSchema";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {PAEnvironment} from "../PA.Core/PAEnvironment";
import {CreateOptions, ESRecordResolver} from "../PA.Core/lib/ESRecordResolver";
import {ASync} from "../PA.Core/Basic/ASync";
import {
    Workflow_Status_Type_Approved,
    Workflow_Status_Type_Closed
} from "../PA.MD.Core/MD.Constants";
import {WorkflowProcessor} from "../PA.Core/Workflow/WorkflowProcessor";
import {IHaveWorkflow, IWorkflowProcessor} from "../PA.Core/PA.Core.IF.Workflow";
import {IPolicyCollection} from "../PA.Core/PA.Core.IF.Policy";
import {PolicyCollection} from "../PA.Core/Security/PolicyCollection";
import {IHaveSecurity} from "../PA.Core/PA.Core.IF.Aspects";
import {ClaimBOStandardWorkflowExtension} from "./ClaimBO.StdWorkflowExt";
import {PolicyBOis} from "../PA.Core/Security/PolicyBOis";
import {
    Role_TPM_Claim_Editor,
    Role_TPM_Claim_Viewer
} from "../PA.Core/Security/Roles";
import {PolicyHasRole} from "../PA.Core/Security/PolicyHasRole";
import {ClaimBOStandardPolicy} from "./ClaimBO.StdPolicy";
import {IPaymentLoadNewOptions} from "./PaymentBO";
import {WorkflowStatusService} from "../PA.MD.Services/WorkflowStatusService";

export interface IClaimLoadNewOptions {
    wbClaimTypeId: number,
    wbClaimCustomerId?: number,
    wbPlanningCustomerId?: number,
    dbContext?: PAdbContext
}

export class ClaimBO
    implements
        IBusinessObject,
        IHaveSecurity,
        IHaveWorkflow
{

    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_Claim: WB_ClaimRow;
    OtherClaimAmounts: { SETTLED_AMT: number, REMAINING_AMT: number, REJECTED_AMT: number };

    constructor(options?: any) {

        this.CreateOptions = options || {};

        if ('dbContext' in this.CreateOptions)
            this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({id: 0, name: 'New Claim'});

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = null;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);

        this.drWB_Claim = null;
        this.OtherClaimAmounts = undefined;



        this.IHaveWorkflow_init()
        this.IHaveSecurity_init()
    }

    get WB_Claim(): WB_ClaimRow {
        return this.drWB_Claim;
    }

    get WB_Claim_Extended(): { SETTLED_AMT: number, REMAINING_AMT: number, REJECTED_AMT: number } {
        return this.OtherClaimAmounts;
    }

    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: IClaimLoadNewOptions): Promise<IBusinessObject> {
        const self = this;

        let opts: IClaimLoadNewOptions = options || <any>{}
        this.LoadProgressProvider.start("Creating Claim");

        self.LoadOptions = opts

        const returnValue = this._OnLoadComplete = (async (): Promise<IBusinessObject> => {

            try {
                if ('dbContext' in self.LoadOptions)
                    self.dbContext = self.LoadOptions.dbContext;
                else
                    self.dbContext = new PAdbContext();

                if (self.dbContext == null) {
                    let sError = "Unable to determine dbContext for New Claim.";
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(sError);
                    throw new Error(sError)
                }

                // Load Type and BU record using dbContext
                let aClmTypeRow: WB_ClaimTypeRow = await self.dbContext.find(esWB_Claim_Type, opts.wbClaimTypeId)
                if (_.isEmpty(aClmTypeRow)) {
                    let sError = "Unable to determine Claim Type for new Claim.";
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(sError);
                    throw new Error(sError)
                }

                let aBusinessUnitRow: WB_BusinessUnitRow = <any>_.first(await self.dbContext.findUsingCache(esWB_BusinessUnit, [aClmTypeRow.cif.WB_BUSINESSUNIT_ID]))
                if (_.isEmpty(aBusinessUnitRow)) {
                    let sError = "Unable to determine Business Unit for new Claim.";
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(sError);
                    throw new Error(sError)
                }

                // Create Claim record using dbContext
                let newClaimRow: WB_ClaimRow = <any> await self.dbContext.createRecordWithKey(esWB_Claim)
                newClaimRow.setDefaults();
                self.drWB_Claim = newClaimRow;
                self.drWB_Claim.cif.WB_BUSINESSUNIT_ID = aClmTypeRow.cif.WB_BUSINESSUNIT_ID
                self.drWB_Claim.WB_BusinessUnitRow = aBusinessUnitRow
                self.drWB_Claim.cif.WB_CLMTYPE_ID = aClmTypeRow.cif.WB_CLMTYPE_ID
                self.drWB_Claim.WB_ClaimTypeRow = aClmTypeRow
                self.drWB_Claim.cif.CURRENCY = aClmTypeRow.cif.CURRENCY_DEFAULT

                if (aClmTypeRow.cif.WORKFLOW_POLICY != 'None') {
                    let initStatus: WB_WorkflowStatusRow = await WorkflowStatusService.getInitialWorkflowStatusByWorkflowWbID(aClmTypeRow.cif.WB_WORKFLOW_ID)
                    if (!_.isEmpty(initStatus)) {
                        self.drWB_Claim.cif.WB_STATUS_ID = initStatus.cif.WB_STATUS_ID
                        self.drWB_Claim.cif.WB_STATUS_ID_NEXT = initStatus.cif.WB_STATUS_ID
                    }
                }

                if (_.isNumber(opts.wbClaimCustomerId)) {
                    let aClaimCustomerRow: WB_CustomerRow = <any>_.first(await self.dbContext.findUsingCache(esWB_Customer, [opts.wbClaimCustomerId]))
                    if (!_.isEmpty(aClaimCustomerRow)) {
                        self.drWB_Claim.cif.WB_CUST_ID_CLAIM = aClaimCustomerRow.cif.WB_CUST_ID
                        self.drWB_Claim.WB_CustomerRow_Claim = aClaimCustomerRow
                    }
                }

                if (_.isNumber(opts.wbPlanningCustomerId)) {
                    let aPlanningCustomerRow: WB_CustomerRow = <any>_.first(await self.dbContext.findUsingCache(esWB_Customer, [opts.wbPlanningCustomerId]))
                    if (!_.isEmpty(aPlanningCustomerRow)) {
                        self.drWB_Claim.cif.WB_CUST_ID_PLANNING = aPlanningCustomerRow.cif.WB_CUST_ID
                        self.drWB_Claim.WB_CustomerRow_Planning = aPlanningCustomerRow
                    }
                }

                await self.IHaveWorkflow_OnLoadComplete()

                await self.IHaveSecurity_OnLoadComplete()

                self.loadOtherAmounts()

                self.Identity.setAll(self.WB_Claim.cif.WB_CLM_ID, self.WB_Claim.cif.CLAIM_ID, self.WB_Claim.cif.CLAIM_COMMENTS);
                self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                self.Identity.listenToRecordUpdates(self.WB_Claim, 'WB_CLM_ID', 'CLAIM_ID', 'CLAIM_COMMENTS');

                self._bIsLoaded = true;
                self.LoadProgressProvider.finish();
                self._OnLoadComplete = null;
                return self;
            } catch (err) {
                self.LoadProgressProvider.exception(err);
                return self
            }
            ;
        })();

        return returnValue;
    }

    Load(anId: number | number, options?: object): Promise<IBusinessObject> {

        const self = this;

        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;

        self.LoadProgressProvider.start("Loading (" + self.Identity.id_ui + ")");

        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {

            if (self.bIsLoaded) {
                let sError = "Claim already loaded.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            self.LoadOptions = options || {};

            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else if (self.dbContext == null)
                self.dbContext = new PAdbContext();

            if (this.dbContext == null) {
                let sError = "Unable to determine dbContext for Claim.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            if ('RecordResolver' in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver;
                recordResolverLoadPromise = ASync.resolve()
            } else {
                recordResolver = ClaimBO.createRecordResolver({dbContext: self.dbContext});
                recordResolverLoadPromise = ClaimBO.setupRecordResolver(recordResolver, [anId])
            }

            recordResolverLoadPromise
                .then(function () {
                    let newClaimRow: any = recordResolver.get(esWB_Claim, anId);

                    if (_.isEmpty(newClaimRow)) {
                        self._OnLoadComplete = null;
                        self.LoadProgressProvider.exception("Id not found.");
                        resolve(self);
                        return;
                    }
                    self.drWB_Claim = newClaimRow;

                    return self.IHaveWorkflow_OnLoadComplete()
                })
                .then(() => {
                    return self.IHaveSecurity_OnLoadComplete()
                }).then(() => {
                self.loadOtherAmounts()

                self.Identity.setAll(self.WB_Claim.cif.WB_CLM_ID, self.WB_Claim.cif.CLAIM_ID, self.WB_Claim.cif.CLAIM_COMMENTS);
                self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                self.Identity.listenToRecordUpdates(self.WB_Claim, 'WB_CLM_ID', 'CLAIM_ID', 'CLAIM_COMMENTS');
                self._bIsLoaded = true;
                self._OnLoadComplete = null;
                self.LoadProgressProvider.finish();
                resolve(self);
            })
                .catch((err: any) => {
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(err);
                    error(err);
                });

        });

        return this._OnLoadComplete;
    }


    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }


    Validate(options?: any): Promise<IBusinessObject> {

        let self = this;
        this.ValidateMessageProvider.clearMessages();
        this.ValidateProgressProvider.start("Validating " + self.drWB_Claim.cif.Claim_NAME + "(" + self.drWB_Claim.cif.Claim_ID + ")");
        return new Promise<IBusinessObject>(async (resolve, reject) => {
            // Does no validations yet
            self.ValidateOptions = options || {};

            if (self.dbContext == null) {
                var sError = "Unable to determine dbContext for to Validate Period.";
                self.ValidateProgressProvider.exception(sError);
                reject(sError);
            }

            if (self.ValidateMessageProvider.countMessages(MessageType.Error) == 0) {
                await self.PrimaryWorkflowProcessor.Validate()
                self.ValidateMessageProvider.addMessages(self.PrimaryWorkflowProcessor.WorkflowMessageProvider.Messages)
            }

            if (self.ValidateMessageProvider.countMessages(MessageType.Error) > 0) {
                self._bIsValidated = false;
            } else {
                self._bIsValidated = true;
            }
            self.ValidateProgressProvider.finish();
            resolve(self);
        });
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options)
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options)
    }

    private SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {

        const self = this;

        this.SaveProgressProvider.start("Saving " + self.WB_Claim.cif.CLAIM_NAME + "(" + self.WB_Claim.cif.CLAIM_ID + ")");

        return new Promise<IBusinessObject>(async (resolve, reject) => {
            // Doesn't need to do anything yet
            // All the records are in the store, since they were added through find or createNewRecord.
            // We could search the tree if there is any record where store isn't set.

            if (self.dbContext == null) {
                self._bIsSaved = false;
                let sError = "Unable to determine dbContext to Save Claim";
                self.SaveProgressProvider.exception(sError);
                reject(sError);
                return
            }


            self.SaveOptions = options || {};
            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>;
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext);
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext);
            }
            try {

                await self.Validate({})
                if (!self.bIsValidated) {
                    self._bIsSaved = false;
                    self.SaveProgressProvider.finish();
                    resolve(self);
                    return;
                }
                await self.PrimaryWorkflowProcessor.Save(false)
                await saveFunction(esWB_Claim, self.drWB_Claim, {})
                if (bBatch) {
                    self.dbContext.addBatchCompleteListener(() => {
                        self._bIsSaved = true;
                    })
                } else {
                    self._bIsSaved = true;
                }
                self.SaveProgressProvider.finish();
                resolve(self);
            } catch (err) {
                self._bIsSaved = false;
                self.SaveProgressProvider.exception(err);
                resolve(self);
                return;
            }
        })
    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    get bIsApproved(): boolean {
        return this.WB_Claim.WB_CurrentWorkflowStatusRow.cif.STATUS_ID == Workflow_Status_Type_Closed
    }

    //Delete
    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options)
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options)
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start("Deleting " + self.drWB_Claim.cif.Claim_NAME + "(" + self.drWB_Claim.cif.Claim_ID + ")");

        return new Promise<IBusinessObject>((resolve, error) => {
            self.drWB_Claim.markDeleted();

            if (bSaveNow) {
                self.SaveUsing(false)
                    .then((ignore) => {
                        self._bIsDeleted = true;
                        self.DeleteProgressProvider.finish();
                        resolve(self);
                    })
                    .catch((err) => {
                        this.DeleteProgressProvider.exception(err);
                        resolve(self)
                    })
            } else {
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                resolve(self);
            }
        });
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    //</editor-fold>

    private loadOtherAmounts() {
        let settled = 0

        _.forEach(this.WB_Claim.WB_ClaimPaymentRows, aRow => {
            if (!_.isEmpty(aRow.WB_PaymentRow.WB_WorkflowStatusRow))
                switch (aRow.WB_PaymentRow.WB_WorkflowStatusRow.cif.STATUS_ID) {
                    case Workflow_Status_Type_Approved:
                    case Workflow_Status_Type_Closed:
                        settled += aRow.cif.PAYMENT_AMT
                        break;
                    default:
                        break;
                }
        })

        this.OtherClaimAmounts = {
            SETTLED_AMT: settled,
            REMAINING_AMT: this.WB_Claim.cif.CLAIM_AMT - settled,
            REJECTED_AMT: this.PrimaryWorkflowProcessor.CurrentState.cif.STATUS_TYPE == Workflow_Status_Type_Closed ? this.WB_Claim.cif.CLAIM_AMT - settled : 0
        }
    }


    async ReloadPayments(): Promise<void> {
        const self = this

        self.dbContext.purgeCache(esWB_Payment_Claim, self.WB_Claim.WB_ClaimPaymentRows)

        let rr = new ESRecordResolver(self.dbContext, {
            standardConditions: {
                [esWB_Payment_Claim]: {"DELETED_IND": {"notIn": ['1']}}
            },
            dctIncludeBelongsToNamedRelations: {
                [esWB_Payment]: [esWB_Workflow_Status, esWB_User, esWB_Payment_Type]
            }
        })

        let payments = await rr.queryAllWithRelationships(esWB_Payment_Claim, {
            where: {
                WB_CLM_ID: self.WB_Claim?.cif.WB_CLM_ID,
                DELETED_IND: {"notIn": ['1']}
            }
        })

        _.forEach(payments, (aPayment: WB_PaymentClaimRow) => {
            self.WB_Claim.WB_ClaimPaymentRows.push(aPayment)
        })

        self.loadOtherAmounts()
        return
    }

    //</editor-fold>

    //<editor-fold desc = "Record Resolver Operations">
    static createRecordResolver(boLoadOptions: any): ESRecordResolver {
        let opts = boLoadOptions || {};
        let dbContext: PAdbContext;

        if ('dbContext' in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }

        const Claim_options: CreateOptions = {
            standardConditions: {
                [esWB_Claim]: {"DELETED_IND": {neq: ['1']}},
                [esWB_Customer]: {"DELETED_IND": {neq: ['1']}},
                [esWB_Claim_Type]: {"DELETED_IND": {neq: ['1']}},
                [esWB_User]: {"DELETED_IND": {neq: ['1']}},
                [esWB_Workflow_Status]: {"DELETED_IND": {neq: ['1']}},
                [esWB_Payment_Claim]: {"DELETED_IND": {neq: ['1']}},
                [esWB_Payment]: {"DELETED_IND": {neq: ['1']}},
                [esWB_Payment_Type]: {"DELETED_IND": {neq: ['1']}},
                [esWB_BusinessUnit]: {"DELETED_IND": {neq: ['1']}}
            },
            dctIncludeBelongsToNamedRelations: {
                [esWB_Payment]: [esWB_Workflow_Status, esWB_User, esWB_Payment_Type],
                [esWB_Payment_Claim]: [esWB_User]
            },

        };

        const aRecResolver = new ESRecordResolver(dbContext, Claim_options);
        return aRecResolver;
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {
        return new Promise<void>((resolve, error) => {
            recordResolver.findAllWithRelationships(esWB_Claim, ids).then((result) => {
                resolve()
            }).catch((err) => {
                error(err)
            })
        })
    }

    static concurrentLoadCountHint(): number {
        return 10;
    }

//</editor-fold>

    //<editor-fold desc = "IHaveSecurity operations">
    private _PolicyCollection: IPolicyCollection;
    private _bIHaveSecurityLoaded: boolean;
    private _OnIHaveSecurityLoaded: Promise<IHaveSecurity>

    IHaveSecurity_init() {
        this._PolicyCollection = new PolicyCollection()
        this._bIHaveSecurityLoaded = false
        this._OnIHaveSecurityLoaded = undefined
    }

    IHaveSecurity_BeforeReload() {
        this._bIHaveSecurityLoaded = false
        this._OnIHaveSecurityLoaded = undefined
    }

    IHaveSecurity_OnLoadComplete(): Promise<void> {
        const self = this

        return new Promise<void>((resolve, error) => {
            self.OnIHaveSecurityLoaded
                .then((ignore) => {
                    self._bIHaveSecurityLoaded = true
                    resolve()
                })
                .catch((err) => {
                    error(err)
                })
        })
    }

    get OnIHaveSecurityLoaded(): Promise<IHaveSecurity> {
        const self = this

        if (self.bIHaveSecurityLoaded) {
            return ASync.resolve(this)
        }

        if (this._OnIHaveSecurityLoaded) {
            return self._OnIHaveSecurityLoaded
        }

        let aHasRolePolicy: PolicyHasRole
        let aStdClaimPolicy: ClaimBOStandardPolicy
        self._OnIHaveSecurityLoaded = new Promise<IHaveSecurity>((resolve, error) => {
            let wbCustId = self.WB_Claim.cif.WB_CUST_ID_CLAIM || self.WB_Claim.cif.WB_CUST_ID_PLANNING
            let aBOis = new PolicyBOis([Role_TPM_Claim_Editor, Role_TPM_Claim_Editor], [Role_TPM_Claim_Viewer], wbCustId)

            self.PolicyCollection.addPolicy("BOis", aBOis)
            aHasRolePolicy = new PolicyHasRole(self.WB_Claim.cif.WB_CUST_ID_PLANNING)
            aHasRolePolicy.OnLoadComplete
                .then((ignored: any) => {
                    self.PolicyCollection.addPolicy("HasRole", aHasRolePolicy)
                    aStdClaimPolicy = new ClaimBOStandardPolicy(self)
                    return aStdClaimPolicy.OnLoadComplete
                })
                .then((ignore) => {
                    self.PolicyCollection.addPolicy("Claim", aStdClaimPolicy)
                    this._bIHaveSecurityLoaded = true
                    resolve()
                })
                .catch((err) => {
                    error(err)
                })

        })
        return this._OnIHaveSecurityLoaded
    }

    get PolicyCollection(): IPolicyCollection {
        return this._PolicyCollection
    }

    get bIHaveSecurityLoaded(): boolean {
        return this._bIHaveSecurityLoaded
    }

    //</editor-fold>


    //<editor-fold desc = "IHaveWorkflow operations">

    private _WorkflowProcessors: IWorkflowProcessor[]
    private _PrimaryWorkflowProcessor: IWorkflowProcessor


    private IHaveWorkflow_init(): void {
        this._WorkflowProcessors = []
        this._PrimaryWorkflowProcessor = undefined
    }

    private IHaveWorkflow_OnLoadComplete(): Promise<void> {
        const self = this

        return new Promise<void>((resolve, error) => {
            // Reset Workflow Processors
            self._WorkflowProcessors = []
            let aWorkflowProcessor
                = new WorkflowProcessor(self.WB_Claim.WB_ClaimTypeRow.cif.WB_WORKFLOW_ID,
                {
                    dbContext: self.dbContext,
                    AppMgr_Id: 'Claim',
                    WB_App_Id: self.WB_Claim.cif.WB_CLM_ID,
                    App_Id: self.WB_Claim.cif.CLAIM_ID,
                    bLoadWorkflowHistory: false,
                    bLoadUserReviewers: false,
                    WB_Cust_Id: self.WB_Claim.cif.WB_CUST_ID_CLAIM,
                    object: self,
                    Record: self.WB_Claim,
                    CurrentStateIdFieldName: 'WB_STATUS_ID',
                    NextStateIdFieldName: 'WB_STATUS_ID_NEXT',
                    NextUserIdFieldName: 'WB_AUTHUSR_ID_WF_NEXT',
                    OwnerUserIdFieldName: 'WB_AUTHUSR_ID_CREATEDBY'
                })
            aWorkflowProcessor.setPolicyFields({
                WorkflowVisible: true,
                CurrentStateVisible: true,
                NextStateVisible: true,
                NextStateEditable: true
            })
            let StdExtension = new ClaimBOStandardWorkflowExtension(self)
            aWorkflowProcessor.setExtensions([StdExtension])
            self._PrimaryWorkflowProcessor = aWorkflowProcessor
            self.PolicyCollection.addPolicy("Workflow", aWorkflowProcessor)

            aWorkflowProcessor.OnLoadComplete
                .then((ignore) => {
                    self._WorkflowProcessors.push(aWorkflowProcessor)
                    resolve()
                })
                .catch((err) => {
                    error(err)
                })

        })
    }

    private IHaveWorkflow_BeforeReload(): void {
        this._WorkflowProcessors = []
        this._PrimaryWorkflowProcessor = undefined
    }

    get WorkflowProcessors(): IWorkflowProcessor[] {
        return this._WorkflowProcessors
    }

    get PrimaryWorkflowProcessor(): IWorkflowProcessor {
        return this._PrimaryWorkflowProcessor
    }


    //</editor-fold>
}

PAEnvironment.registerBusinessObjectClass(ClaimBO, "ClaimBO", "Claim")